import { toast } from "@/components/ui/use-toast";
import { HslColor } from "@uiw/react-color";

interface PolygonData {
  id: number;
  name: string;
  description?: string;
  fillColor: string; // ex: "#000000"
  strokeColor: string; // ex: "#ff0000"
  strokeWidth: number;
  opacity: number; // ex: 0.7
  coordinates: [number, number][];
}

function rgbaHexToKmlColor(hex: string, opacity: number) {
  // hex: "#rrggbb" => KML format: aabbggrr (little endian with alpha)
  const r = hex.slice(1, 3);
  const g = hex.slice(3, 5);
  const b = hex.slice(5, 7);
  const a = Math.round(opacity * 255)
    .toString(16)
    .padStart(2, "0");
  return `${a}${b}${g}${r}`.toLowerCase();
}

export async function copyKmlToClipboard(polygon: PolygonData) {
  const {
    name,
    description,
    fillColor,
    strokeColor,
    strokeWidth,
    opacity,
    coordinates,
  } = polygon;
  const kmlFillColor = rgbaHexToKmlColor(fillColor, opacity);
  const kmlStrokeColor = rgbaHexToKmlColor(strokeColor, 1);

  const coordString = [...coordinates, coordinates[0]] // fermer le polygone
    .map(([lng, lat]) => `${lng},${lat},0`)
    .join(" ");

  const kml = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <Style id="polygonStyle">
      <LineStyle>
        <color>${kmlStrokeColor}</color>
        <width>${strokeWidth}</width>
      </LineStyle>
      <PolyStyle>
        <color>${kmlFillColor}</color>
      </PolyStyle>
    </Style>
    <Placemark>
      <name>${name}</name>
      ${description ? `<description>${description}</description>` : ""}
      <styleUrl>#polygonStyle</styleUrl>
      <Polygon>
        <outerBoundaryIs>
          <LinearRing>
            <coordinates>
              ${coordString}
            </coordinates>
          </LinearRing>
        </outerBoundaryIs>
      </Polygon>
    </Placemark>
  </Document>
</kml>`;

  try {
    await navigator.clipboard.writeText(kml);
    toast({
      description: "Le fichier KML a été copié dans le presse-papiers",
    });
  } catch (error: any) {
    toast({
      description: error?.message || "Une erreur inattendue est survenue",
      variant: "destructive",
    });
  }
}

export const listColors = [
  "#D50000",
  "#C51162",
  "#2962FF",
  "#00695C",
  "#2E7D32",
  "#FF6F00",
  "#E53935",
  "#8E24AA",
  "#1E88E5",
  "#00ACC1",
  "#43A047",
  "#FDD835",
  "#FFFFFF",
  "#E0E0E0",
  "#9E9E9E",
  "#616161",
  "#000000",
  "#6D4C41",
];
type CommonPolygon = {
  codeArr?: string;
  codeCom?: string;
  codeDep?: string;
  codeInsee?: string;
  comAbs?: string;
  contenance?: string;
  feuille?: string;
  idu?: string;
  nomCom?: string;
  numero?: string;
  section?: string;
  surface?: string;
  pays?: string;

  id: string;
  name: string;
  description: string;
  fillColor: string;
  strokeColor: string;
  strokeWidth: number;
  opacity: number;
  visible: boolean;
  coordinates: [number, number][];
  project?: number;
  folder?: number;
};

type FolderWithData = {
  id: number;
  name: string;
  locations: CommonPolygon[];
  parcels: CommonPolygon[];
};

export const exportToKml = (folders: FolderWithData[], name: string) => {
  const today = new Date().toISOString().split("T")[0];
  const filename = `${name}-${today}.kml`;
  const kmlHeader = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>`;
  const kmlFooter = `</Document></kml>`;

  const folderElements = folders
    .map((folder) => {
      const allPolygons = [
        ...(folder.locations || []),
        ...(folder.parcels || []),
      ];
      const placemarks = allPolygons
        .map((poly) => {
          const coords = [...poly.coordinates];
          if (
            coords.length > 2 &&
            (coords[0][0] !== coords[coords.length - 1][0] ||
              coords[0][1] !== coords[coords.length - 1][1])
          ) {
            coords.push(coords[0]); // boucle fermée
          }

          const coordinateString = coords
            .map(([lat, lng]) => `${lng},${lat},0`)
            .join(" ");

          const isParcel = !!poly.idu || !!poly.codeInsee;

          const extendedData = isParcel
            ? `
        <ExtendedData>
          ${
            poly.idu ? `<Data name="idu"><value>${poly.idu}</value></Data>` : ""
          }
          ${
            poly.codeInsee
              ? `<Data name="codeInsee"><value>${poly.codeInsee}</value></Data>`
              : ""
          }
          ${
            poly.nomCom
              ? `<Data name="nomCom"><value>${poly.nomCom}</value></Data>`
              : ""
          }
          ${
            poly.section
              ? `<Data name="section"><value>${poly.section}</value></Data>`
              : ""
          }
          ${
            poly.numero
              ? `<Data name="numero"><value>${poly.numero}</value></Data>`
              : ""
          }
          ${
            poly.feuille
              ? `<Data name="feuille"><value>${poly.feuille}</value></Data>`
              : ""
          }
          ${
            poly.contenance
              ? `<Data name="contenance"><value>${poly.contenance}</value></Data>`
              : ""
          }
          ${
            poly.surface
              ? `<Data name="surface"><value>${poly.surface}</value></Data>`
              : ""
          }
          ${
            poly.codeDep
              ? `<Data name="codeDep"><value>${poly.codeDep}</value></Data>`
              : ""
          }
          ${
            poly.codeCom
              ? `<Data name="codeCom"><value>${poly.codeCom}</value></Data>`
              : ""
          }
          ${
            poly.codeArr
              ? `<Data name="codeArr"><value>${poly.codeArr}</value></Data>`
              : ""
          }
          ${
            poly.pays
              ? `<Data name="pays"><value>${poly.pays}</value></Data>`
              : ""
          }
        </ExtendedData>`
            : "";

          return `
      <Placemark>
        <name><![CDATA[${poly.name || "Sans nom"}]]></name>
        <description><![CDATA[${poly.description || ""}]]></description>
        ${extendedData}
        <Style>
          <LineStyle>
            <color>${toKmlColor(poly.strokeColor, poly.opacity)}</color>
            <width>${poly.strokeWidth}</width>
          </LineStyle>
          <PolyStyle>
            <color>${toKmlColor(poly.fillColor, poly.opacity)}</color>
          </PolyStyle>
        </Style>
        <Polygon>
          <outerBoundaryIs>
            <LinearRing>
              <coordinates>${coordinateString}</coordinates>
            </LinearRing>
          </outerBoundaryIs>
        </Polygon>
      </Placemark>`;
        })
        .join("\n");

      return `
    <Folder id="${folder.id}">
      <name><![CDATA[${folder.name}]]></name>
      <id><![CDATA[${folder.id}]]></id>
      ${placemarks}
    </Folder>`;
    })
    .join("\n");

  const kml = `${kmlHeader}\n${folderElements}\n${kmlFooter}`;
  const blob = new Blob([kml], {
    type: "application/vnd.google-earth.kml+xml",
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Convertit couleur hex + opacité vers format KML (aabbggrr)
function toKmlColor(hex: string, opacity: number): string {
  const cleanHex = hex.replace("#", "");
  const r = parseInt(cleanHex.slice(0, 2), 16);
  const g = parseInt(cleanHex.slice(2, 4), 16);
  const b = parseInt(cleanHex.slice(4, 6), 16);
  const a = Math.round(opacity * 255);
  return [a, b, g, r]
    .map((x) => x.toString(16).padStart(2, "0"))
    .join("")
    .toLowerCase();
}

export interface HslColor {
  h: number; // 0 - 360
  s: number; // 0 - 100
  l: number; // 0 - 100
}

/**
 * Convertit un objet HSL en code hexadécimal (#rrggbb)
 */
export function hslToHex({ h, s, l }: HslColor): string {
  // Convertir saturation et lightness en 0..1
  s /= 100;
  l /= 100;

  // Fonction auxiliaire (identique à hueToHex dans ton exemple)
  function hueToRgb(p: number, q: number, t: number): number {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  }

  let r: number, g: number, b: number;

  if (s === 0) {
    // Ton de gris
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const hk = h / 360;

    r = hueToRgb(p, q, hk + 1 / 3);
    g = hueToRgb(p, q, hk);
    b = hueToRgb(p, q, hk - 1 / 3);
  }

  // Convertir r,g,b [0..1] en hex [00..ff]
  const toHex = (x: number) =>
    Math.round(x * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export interface HslColor {
  h: number;
  s: number;
  l: number;
}

/**
 * Parse une chaîne HSL du type "219.7, 56.7%, 38.2%" en objet HslColor
 */
export function parseHslString(hslString: string): HslColor | null {
  if (!hslString) return null;

  // Sépare par virgule
  const parts = hslString.split(",").map((p) => p.trim());

  if (parts.length !== 3) return null;

  // Enlève le % sur s et l puis parseFloat
  const h = parseFloat(parts[0]);
  const s = parseFloat(parts[1].replace("%", ""));
  const l = parseFloat(parts[2].replace("%", ""));

  if ([h, s, l].some((v) => isNaN(v))) return null;

  return { h, s, l };
}

/**
 * Convertit une couleur hexadécimale en HSL.
 *
 * @param hex La chaîne hexadécimale (ex : "#RRGGBB" ou "RRGGBB").
 * @returns Un objet { h, s, l } — h en degrés (0-360), s et l en pourcentages (0-100).
 */
export function hexToHsl(
  hex: string
): { h: number; s: number; l: number } | null {
  const hexValue = hex.replace("#", "").trim();

  // Format hex invalide
  if (!/^([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hexValue)) {
    console.error("Format hexadécimal invalide. Utilisez #RGB ou #RRGGBB.");
    return null;
  }

  // Expand short form (e.g. "03F") to full form ("0033FF")
  const fullHex =
    hexValue.length === 3
      ? hexValue
          .split("")
          .map((char) => char + char)
          .join("")
      : hexValue;

  const r = parseInt(fullHex.slice(0, 2), 16) / 255;
  const g = parseInt(fullHex.slice(2, 4), 16) / 255;
  const b = parseInt(fullHex.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0,
    s = 0,
    l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }

    h *= 60;
  }

  return {
    h: Math.round(h),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}
